/* eslint-disable no-console */
/**
 * FullStory session-lifecycle instrumentation utilities.
 *
 * All functions are no-ops when:
 *  - FullStory is disabled (window.FS not present)
 *  - Running server-side (no window)
 *  - sessionStorage is unavailable
 *
 * These events are designed to be compared across deployments in the
 * FullStory dashboard to measure the impact of performance improvements
 * (e.g. SSR, OIDC BFF) on real user load times.
 *
 * Event timeline (in order of firing within a session):
 *
 *   bundle_evaluated       — JS bundle parsed and React tree initialising (ClientRoot mount)
 *   app_entry              — auth state known for the first time
 *   auth_redirect_started  — browser is about to leave for SSO (fired 0–N times)
 *   auth_redirect_returned — browser is back from SSO (fired 0–N times)
 *   user_ready             — identity resolved, org picker accessible
 *   org_ready              — org claim verified, user is inside an org
 *   page_interactive       — workspace content visible
 */

const ENTRY_TS_KEY = 'fs_app_entry_ts';
const REDIRECT_COUNT_KEY = 'fs_auth_redirect_count';
const USER_READY_FIRED_KEY = 'fs_user_ready_fired';
const ORG_READY_TS_KEY = 'fs_org_ready_ts';
const ORG_READY_FIRED_KEY = 'fs_org_ready_fired';
const PAGE_INTERACTIVE_FIRED_KEY = 'fs_page_interactive_fired';

type AuthProvider = 'auth0' | 'okta';

/**
 * Derives the auth provider from the build-time env var.
 * Mirrors the pattern used in CommonAuthProvider.tsx.
 */
function getAuthProvider(): AuthProvider {
  return process.env.AUTH_CLIENT_PROVIDER === 'auth0' ? 'auth0' : 'okta';
}

/**
 * Returns milliseconds elapsed since markAppEntry was first called,
 * or null if the entry timestamp is missing.
 */
export function getEntryDeltaMs(): number | null {
  try {
    const ts = sessionStorage.getItem(ENTRY_TS_KEY);

    if (!ts) {
      return null;
    }

    return Date.now() - Number(ts);
  } catch {
    return null;
  }
}

/**
 * Records the very first moment the app is loaded in this browser session.
 * Safe to call from multiple components (page.tsx + AuthGuard) — the
 * sessionStorage gate ensures the timestamp and event fire exactly once.
 *
 * @param isAuthenticated - current auth state at the time of the call
 */
export function markAppEntry(isAuthenticated: boolean): void {
  try {
    if (sessionStorage.getItem(ENTRY_TS_KEY)) {
      return;
    }

    sessionStorage.setItem(ENTRY_TS_KEY, String(Date.now()));

    window.FS?.event?.('app_entry', {
      auth_provider: getAuthProvider(),
      is_authenticated: isAuthenticated,
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark app entry, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment a redirect to an SSO provider is initiated.
 * Increments a redirect counter so app_ready can report the total
 * number of SSO round-trips in the session.
 *
 * Call this immediately before signinRedirect / loginWithRedirect.
 *
 * @param provider - 'auth0' or 'okta'
 * @param fromRoute - window.location.pathname at the moment of redirect
 */
export function markAuthRedirectStarted(provider: AuthProvider, fromRoute: string): void {
  try {
    const count = Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0') + 1;

    sessionStorage.setItem(REDIRECT_COUNT_KEY, String(count));

    window.FS?.event?.('auth_redirect_started', {
      auth_provider: provider,
      from_route: fromRoute,
      redirect_count: count,
    });
  } catch {
    console.warn('Failed to mark auth redirect returned, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the app receives control back from the SSO provider
 * (i.e. the /callback page has finished processing the token exchange).
 *
 * @param provider - 'auth0' or 'okta'
 * @param success - true if token exchange succeeded, false on error
 */
export function markAuthRedirectReturned(provider: AuthProvider, success: boolean): void {
  try {
    window.FS?.event?.('auth_redirect_returned', {
      auth_provider: provider,
      callback_route: window.location.pathname,
      success,
    });
  } catch {
    console.warn('Failed to mark auth redirect returned, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the user's identity is resolved:
 * auth complete + user data (memberships, profile) loaded.
 * The user is now able to choose an organisation.
 *
 * Fires at most once per browser session (sessionStorage gate).
 * In sessions where OrganizationAuthGuard does a re-login, this fires
 * before that redirect — use org_ready for the true end-to-end time.
 */
export function markUserReady(): void {
  try {
    if (sessionStorage.getItem(USER_READY_FIRED_KEY)) {
      return;
    }

    sessionStorage.setItem(USER_READY_FIRED_KEY, '1');

    window.FS?.event?.('user_ready', {
      auth_provider: getAuthProvider(),
      time_since_entry_ms: getEntryDeltaMs(),
      redirect_count: Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0'),
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark user ready, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the client-side JS bundle has been parsed and the React
 * tree is beginning to initialise. Fired as early as possible in ClientRoot —
 * the first client component in the tree.
 *
 * The delta between this and app_entry tells you how much time is spent waiting
 * for the JS bundle vs waiting for auth. After SSR/RSC improvements this number
 * should drop significantly because the server sends useful HTML before the
 * bundle is evaluated.
 */
export function markBundleEvaluationTime(): void {
  try {
    window.FS?.event?.('bundle_evaluated', {
      time_since_entry_ms: getEntryDeltaMs(),
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark bundle evaluation time, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the user has fully landed in an organisation:
 * org claim verified, org context authorised, content rendered.
 *
 * Fires at most once per browser session (sessionStorage gate) —
 * subsequent org navigations within the same session are not tracked.
 * This is the primary KPI for end-to-end load time including any
 * org-scoped re-auth redirects triggered by OrganizationAuthGuard.
 *
 * @param organizationId - the org the user successfully landed in
 */
export function markOrgReady(organizationId: string): void {
  try {
    if (sessionStorage.getItem(ORG_READY_FIRED_KEY)) {
      return;
    }

    sessionStorage.setItem(ORG_READY_FIRED_KEY, '1');
    sessionStorage.setItem(ORG_READY_TS_KEY, String(Date.now()));

    window.FS?.event?.('org_ready', {
      auth_provider: getAuthProvider(),
      organization_id: organizationId,
      time_since_entry_ms: getEntryDeltaMs(),
      redirect_count: Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0'),
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark org ready, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the workspace content is visible and interactive —
 * the loading state has resolved and real content is rendered.
 *
 * Fires at most once per browser session (sessionStorage gate).
 * The delta between org_ready and page_interactive isolates Next.js
 * rendering and data-fetching time from auth time. This is the metric
 * that will move when SSR / RSC / dependency optimisations are shipped,
 * independent of any auth flow improvements.
 */
export function markPageInteractive(): void {
  try {
    if (sessionStorage.getItem(PAGE_INTERACTIVE_FIRED_KEY)) {
      return;
    }

    sessionStorage.setItem(PAGE_INTERACTIVE_FIRED_KEY, '1');

    const orgReadyTs = sessionStorage.getItem(ORG_READY_TS_KEY);
    const timeSinceOrgReadyMs = orgReadyTs ? Date.now() - Number(orgReadyTs) : null;

    window.FS?.event?.('page_interactive', {
      auth_provider: getAuthProvider(),
      time_since_entry_ms: getEntryDeltaMs(),
      time_since_org_ready_ms: timeSinceOrgReadyMs,
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark page interactive, sessionStorage or FS unavailable');
  }
}
